import {
  Address,
  MarketMeta,
  MarketUid,
  ProtocolKeyService,
} from "@silo/types";
import { Effect, pipe, Array, HashMap, Option } from "effect";
import { KvClient } from "../kv-client";
import { BlobClient } from "../blob-client";
import { ContractsClient } from "../contracts-client";

export function getMarketMetaArray(marketAddresses: Address[]) {
  return Effect.gen(function* () {
    yield* Effect.logInfo("Begin 🎬: get market meta array");

    const protocolKey = yield* ProtocolKeyService;

    const marketUids = pipe(
      marketAddresses,
      Array.map(
        (marketAddress): MarketUid => ({
          protocolKey,
          marketAddress,
        })
      )
    );

    const marketMetaArray: MarketMeta[] = yield* Effect.firstSuccessOf([
      pipe(
        KvClient.getMarketMetaArray(marketUids),
        Effect.tap(() =>
          Effect.logInfo("Complete ✅: get market meta array with kv strategy")
        )
      ),
      pipe(
        getStaticMarketMetaArray(marketAddresses),
        Effect.tap(() =>
          Effect.logInfo(
            "Complete ✅: get market meta array with static strategy"
          )
        )
      ),
    ]);

    return marketMetaArray;
  });
}

function getStaticMarketMetaArray(marketAddresses: Address[]) {
  return Effect.gen(function* () {
    const protocolKey = yield* ProtocolKeyService;
    const staticMarkets = yield* BlobClient.getProtocolStaticMarkets(
      protocolKey
    );

    const hashMap = pipe(
      staticMarkets,
      Array.map((market) => [market.marketAddress, market] as const),
      HashMap.fromIterable
    );

    const userStaticMarkets = pipe(
      marketAddresses,
      Array.flatMapNullable((marketAddress) => {
        const staticMarket = HashMap.get(hashMap, marketAddress);

        return Option.getOrNull(staticMarket);
      })
    );

    const marketMetaArray: MarketMeta[] = yield* Effect.all(
      userStaticMarkets.map((staticMarket) =>
        ContractsClient.getMarketMeta(staticMarket)
      ),
      { concurrency: "unbounded" }
    );

    return marketMetaArray;
  });
}
